import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  fetchAllClientsForMatching,
  planKycProfileFill,
  applyKycProfileFill,
} from "../../../../services/kycService";
import { APP_USER_STATUS } from "../../../../Utils/kycConstants";

const fieldSx = {
  mb: 2,
  "& .MuiInputBase-root": { color: "#fff" },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
};

const SOURCE = "admin_add_single_client";

/**
 * Add one approved client to KYC (or fill the empty fields of an existing profile).
 * Preview shows field names only; Save re-checks every field inside a transaction
 * and writes only fields that are still empty.
 */
export default function KycAddClientDialog({ open, onClose, existingProfileIds, onDone }) {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selected, setSelected] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setPlan(null);
    setResult(null);
    setError(null);
    setLoadingClients(true);
    fetchAllClientsForMatching()
      .then((list) => setClients(list.filter((c) => c.status === APP_USER_STATUS.APPROVED)))
      .catch(() => setError("Could not load clients."))
      .finally(() => setLoadingClients(false));
  }, [open]);

  const options = useMemo(() => {
    const has = existingProfileIds || new Set();
    return clients
      .map((c) => ({ ...c, hasProfile: has.has(c.user_id) }))
      .sort((a, b) => {
        if (a.hasProfile !== b.hasProfile) return a.hasProfile ? 1 : -1;
        return (a.full_name || "").localeCompare(b.full_name || "", "pt-BR", { sensitivity: "base" });
      });
  }, [clients, existingProfileIds]);

  const runPreview = async (client) => {
    setPlan(null);
    setResult(null);
    setError(null);
    if (!client) return;
    setPlanning(true);
    try {
      setPlan(await planKycProfileFill(client.user_id, { source: SOURCE, matchMethod: "firebase_user_single" }));
    } catch (e) {
      setError(`Preview failed (${e.code || "error"}).`);
    } finally {
      setPlanning(false);
    }
  };

  const handleSave = async () => {
    if (!selected || !plan) return;
    setSaving(true);
    setError(null);
    try {
      const res = await applyKycProfileFill(selected.user_id, {
        expectedSignature: plan.signature,
        source: SOURCE,
        matchMethod: "firebase_user_single",
      });
      setResult(res);
      setPlan(null);
      onDone?.();
    } catch (e) {
      if (e.code === "plan_changed") {
        setError("KYC data changed since the preview. The preview was refreshed — review it and save again.");
        await runPreview(selected);
      } else {
        setError(`Save failed (${e.code || "error"}). Nothing was written.`);
      }
    } finally {
      setSaving(false);
    }
  };

  const canSave = !!plan && plan.filledCount > 0 && !planning && !saving;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: "#1a1a2e", color: "#fff" } }}
    >
      <DialogTitle>Add client to KYC</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 13, mb: 2 }}>
          Approved clients only. Only empty fields are filled — anything already on the KYC
          profile is kept as it is. Nothing is deleted and no e-mail is sent.
        </Typography>
        <Autocomplete
          options={options}
          loading={loadingClients}
          getOptionLabel={(o) => `${o.full_name || "—"} (${o.email || ""})`}
          isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
          renderOption={(props, o) => (
            <li {...props} key={o.user_id}>
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", gap: 1 }}>
                <span>{`${o.full_name || "—"} (${o.email || ""})`}</span>
                {o.hasProfile && (
                  <Chip size="small" label="In KYC" variant="outlined" sx={{ fontSize: 11, color: "#aaa" }} />
                )}
              </Box>
            </li>
          )}
          value={selected}
          onChange={(_, v) => {
            setSelected(v);
            runPreview(v);
          }}
          disabled={saving}
          renderInput={(params) => (
            <TextField {...params} label="Client" size="small" sx={fieldSx} />
          )}
          sx={{ mb: 1, "& .MuiAutocomplete-popupIndicator": { color: "rgba(255,255,255,0.5)" } }}
        />

        {selected && (
          <Typography variant="caption" sx={{ color: "#888", display: "block", mb: 1.5 }}>
            uid: {selected.user_id}
          </Typography>
        )}

        {planning && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#aaa" }}>
            <CircularProgress size={16} color="inherit" />
            <Typography variant="body2">Building preview…</Typography>
          </Box>
        )}

        {plan && !planning && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            {plan.filledCount === 0 ? (
              <Typography variant="body2" sx={{ color: "#aaa" }}>
                Nothing to fill — every field this action manages is already filled.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" sx={{ color: "#2af598", fontWeight: 700, mb: 1 }}>
                  {plan.created ? "Will create the KYC profile" : "Will fill empty fields only"} ·{" "}
                  {plan.filledCount} field{plan.filledCount === 1 ? "" : "s"}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {plan.fields.map((f) => (
                    <Chip
                      key={f}
                      size="small"
                      label={f}
                      variant="outlined"
                      sx={{ fontSize: 11, color: "#ddd", borderColor: "rgba(42,245,152,0.4)" }}
                    />
                  ))}
                </Box>
              </>
            )}
            {plan.keptCount > 0 && (
              <Typography variant="caption" sx={{ color: "#888", display: "block", mt: 1 }}>
                {plan.keptCount} already-filled field{plan.keptCount === 1 ? "" : "s"} kept unchanged.
              </Typography>
            )}
          </Box>
        )}

        {result && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {result.created ? "Profile created" : "Profile updated"} · {result.filledCount} field
            {result.filledCount === 1 ? "" : "s"} filled · uid {result.uid}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: "rgba(255,255,255,0.7)" }}>
          {result ? "Close" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={handleSave}
          sx={{ bgcolor: "#2af598", color: "#0a1a12" }}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
